import { now, toCalendarDate } from '@internationalized/date'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { fetchPeriodEntries } from '@/data/ledger'
import { listEnvelopes, saveEnvelope } from '@/data/envelopes'
import { listGoals, saveGoal } from '@/data/goals'
import { listOpenPastPeriods } from '@/data/period-close'
import {
  replaceOperation,
  submitOperation,
  type RemoteOperationResult,
  type ReplacementCommand,
} from '@/data/operations'
import { fetchPeriodState, type PeriodSnapshot } from '@/data/period-state'
import { listRecurringRules, saveRecurringRule } from '@/data/recurring'
import { supabase } from '@/data/supabase'
import {
  computeLedgerState,
  effectiveEntries,
  envelopeInsight,
  isActive,
  occurrencesInPeriod,
  periodBounds,
  totalAvailable,
  type Envelope,
  type EnvelopeInsight,
  type Goal,
  type LedgerEntry,
  type Occurrence,
  type OperationCommand,
  type RecurringRule,
} from '@/domain'

import { stableUuid } from './stable-id'

/**
 * État budgétaire de la période en cours : enveloppes, règles récurrentes, journal et soldes.
 * La base calcule les soldes de référence ; le domaine les recalcule à l'identique à partir du
 * journal (affichage immédiat, et base du mode hors ligne en Phase 9).
 */
export const useBudgetStore = defineStore('budget', () => {
  const envelopes = ref<Envelope[]>([])
  const rules = ref<RecurringRule[]>([])
  const goals = ref<Goal[]>([])
  /** Mois passés sans bilan, du plus ancien au plus récent. */
  const pendingReviews = ref<string[]>([])
  const snapshot = ref<PeriodSnapshot | null>(null)
  const entries = ref<LedgerEntry[]>([])
  const loading = ref(false)
  const loaded = ref(false)

  const activeEnvelopes = computed(() =>
    envelopes.value.filter(isActive).sort((a, b) => a.sortOrder - b.sortOrder),
  )

  const settings = computed(() =>
    snapshot.value
      ? { timeZone: snapshot.value.timeZone, startDay: snapshot.value.startDay }
      : null,
  )

  const bounds = computed(() =>
    snapshot.value && settings.value ? periodBounds(snapshot.value.periodId, settings.value) : null,
  )

  const today = computed(() => toCalendarDate(now(settings.value?.timeZone ?? 'Africa/Porto-Novo')))

  const state = computed(() =>
    snapshot.value ? computeLedgerState(snapshot.value.opening, entries.value) : null,
  )

  /** Échéances réglées : périodes antérieures (base) et période en cours (journal). */
  const settled = computed(() => {
    const ids = new Set(snapshot.value?.history.settledOccurrences ?? [])
    for (const entry of effectiveEntries(entries.value)) {
      if ((entry.kind === 'income' || entry.kind === 'expense') && entry.occurrenceId) {
        ids.add(entry.occurrenceId)
      }
    }
    return ids
  })

  function occurrencesOf(
    direction: 'income' | 'expense',
  ): (Occurrence & { rule: RecurringRule })[] {
    const period = bounds.value
    if (!period) return []
    return rules.value
      .filter((rule) => rule.direction === direction && rule.isActive)
      .flatMap((rule) =>
        occurrencesInPeriod(rule, period).map((occurrence) => ({ ...occurrence, rule })),
      )
      .sort((a, b) => a.dueOn.compare(b.dueOn))
  }

  /** Revenus attendus et pas encore confirmés : affichés à part, jamais dépensables. */
  const expectedIncome = computed(() =>
    occurrencesOf('income').filter((occurrence) => !settled.value.has(occurrence.id)),
  )

  const expectedIncomeTotal = computed(() =>
    expectedIncome.value.reduce((total, occurrence) => total + occurrence.expectedAmount, 0),
  )

  const insights = computed<Record<string, EnvelopeInsight>>(() => {
    const current = state.value
    const period = bounds.value
    if (!current || !period) return {}
    return Object.fromEntries(
      activeEnvelopes.value.map((envelope) => [
        envelope.id,
        envelopeInsight(envelope, current, period, today.value),
      ]),
    )
  })

  /** Échéances dont le montant est déjà mis de côté (ce mois-ci ou reporté). */
  const reservedOccurrences = computed(() => {
    const ids = new Set(Object.keys(snapshot.value?.history.openReservations ?? {}))
    for (const [id, amount] of Object.entries(state.value?.reservedByOccurrence ?? {})) {
      if (amount > 0) ids.add(id)
      else ids.delete(id)
    }
    return ids
  })

  const available = computed(() =>
    state.value
      ? totalAvailable(
          state.value,
          activeEnvelopes.value.map((envelope) => envelope.id),
        )
      : 0,
  )

  async function refresh(): Promise<void> {
    loading.value = true
    try {
      const [nextEnvelopes, nextRules, nextSnapshot, nextGoals] = await Promise.all([
        listEnvelopes(supabase),
        listRecurringRules(supabase),
        fetchPeriodState(supabase),
        listGoals(supabase),
      ])
      envelopes.value = nextEnvelopes
      rules.value = nextRules
      goals.value = nextGoals
      snapshot.value = nextSnapshot
      const [nextEntries, openPast] = await Promise.all([
        fetchPeriodEntries(supabase, nextSnapshot.periodId),
        listOpenPastPeriods(supabase, nextSnapshot.periodId).catch(() => pendingReviews.value),
      ])
      entries.value = nextEntries
      pendingReviews.value = openPast
      loaded.value = true
    } finally {
      loading.value = false
    }
    await reserveUpcoming()
  }

  let reserving = false

  /**
   * Met de côté le montant des échéances marquées « réserver à l'avance » dès que l'enveloppe
   * le permet (spec §3.4). Sans fonds suffisants, rien n'est forcé : la réservation se fera
   * après la prochaine répartition. Identifiant déterministe : jamais de double réservation.
   */
  async function reserveUpcoming(): Promise<void> {
    if (reserving || snapshot.value?.status !== 'open') return
    reserving = true
    try {
      const pending = occurrencesOf('expense').filter(
        (occurrence) =>
          occurrence.rule.reserveInAdvance &&
          occurrence.rule.envelopeId !== null &&
          !settled.value.has(occurrence.id) &&
          !reservedOccurrences.value.has(occurrence.id),
      )
      let changed = false
      const spare: Record<string, number> = {}
      for (const occurrence of pending) {
        const envelopeId = occurrence.rule.envelopeId
        if (envelopeId === null || !activeEnvelopes.value.some((item) => item.id === envelopeId)) {
          continue
        }
        const left = spare[envelopeId] ?? insights.value[envelopeId]?.available ?? 0
        if (occurrence.expectedAmount > left) continue
        const result = await submitOperation(
          supabase,
          {
            operationId: await stableUuid(`reserve:${occurrence.id}`),
            occurredAt: new Date().toISOString(),
          },
          {
            kind: 'reserve',
            envelopeId,
            occurrenceId: occurrence.id,
            amount: occurrence.expectedAmount,
          },
        )
        if (result.ok) {
          changed = true
          spare[envelopeId] = left - occurrence.expectedAmount
        }
      }
      if (changed) {
        snapshot.value = await fetchPeriodState(supabase)
        entries.value = await fetchPeriodEntries(supabase, snapshot.value.periodId)
      }
    } catch {
      // Réseau : la réservation sera retentée au prochain chargement.
    } finally {
      reserving = false
    }
  }

  async function ensureLoaded(): Promise<void> {
    if (!loaded.value) await refresh()
  }

  /** Envoie une opération ; en cas de succès, recharge l'état calculé par la base. */
  /**
   * `operationId` : à réutiliser pour réessayer la même opération sans risque de doublon
   * (la base reconnaît l'identifiant et ne l'enregistre qu'une fois).
   */
  async function run(
    command: OperationCommand,
    options: { occurredAt?: string; operationId?: string } = {},
  ): Promise<RemoteOperationResult> {
    const result = await submitOperation(
      supabase,
      {
        operationId: options.operationId ?? crypto.randomUUID(),
        occurredAt: options.occurredAt ?? new Date().toISOString(),
      },
      command,
    )
    if (result.ok) await refresh()
    return result
  }

  /**
   * Modifie une opération du mois : la base annule l'originale et enregistre la nouvelle version
   * dans une seule transaction. Les identifiants sont fournis par l'appelant (nouvel essai sûr).
   */
  async function replace(
    targetOperationId: string,
    command: ReplacementCommand,
    options: { operationId: string; reversalId: string; occurredAt?: string },
  ): Promise<RemoteOperationResult> {
    const result = await replaceOperation(
      supabase,
      {
        operationId: options.operationId,
        reversalId: options.reversalId,
        targetOperationId,
        occurredAt: options.occurredAt ?? new Date().toISOString(),
      },
      command,
    )
    if (result.ok) await refresh()
    return result
  }

  async function putEnvelope(envelope: Envelope): Promise<Envelope> {
    const saved = await saveEnvelope(supabase, envelope)
    const index = envelopes.value.findIndex((candidate) => candidate.id === saved.id)
    if (index === -1) envelopes.value = [...envelopes.value, saved]
    else
      envelopes.value = envelopes.value.map((candidate) =>
        candidate.id === saved.id ? saved : candidate,
      )
    return saved
  }

  async function putRule(rule: RecurringRule): Promise<RecurringRule> {
    const saved = await saveRecurringRule(supabase, rule)
    rules.value = [...rules.value.filter((candidate) => candidate.id !== saved.id), saved]
    return saved
  }

  /** Enregistre un objectif ; un objectif archivé quitte la liste. */
  async function putGoal(goal: Goal): Promise<Goal> {
    const saved = await saveGoal(supabase, goal)
    const others = goals.value.filter((candidate) => candidate.id !== saved.id)
    goals.value = saved.archivedAt === null ? [...others, saved] : others
    return saved
  }

  function reset(): void {
    envelopes.value = []
    rules.value = []
    goals.value = []
    pendingReviews.value = []
    snapshot.value = null
    entries.value = []
    loaded.value = false
  }

  return {
    envelopes,
    activeEnvelopes,
    rules,
    goals,
    pendingReviews,
    snapshot,
    entries,
    state,
    bounds,
    today,
    settled,
    expectedIncome,
    expectedIncomeTotal,
    insights,
    reservedOccurrences,
    available,
    loading,
    loaded,
    refresh,
    ensureLoaded,
    run,
    replace,
    putEnvelope,
    putRule,
    putGoal,
    reset,
    occurrencesOf,
  }
})
